import { Suspense, memo, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree, useLoader } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Environment } from "@react-three/drei";
import { CalloutProjector, CalloutOverlay, useCalloutRefs } from "./Callouts";
import { REF_H } from "../lib/organs";
import { loadModel, blend, blendJoints, bodyHeight, SUB_DIRS, BACK_ZONES } from "../lib/bodyModel";
import { Skeleton, Arteries, FaintLayers } from "./Anatomy";
import { CLIP, clipInside, clipOutside } from "../lib/clip";
import { Hair } from "./Hair";
import { SUBZONES } from "../lib/i18n";
import { organsFor } from "../lib/organs";
import { Organs } from "./Organs";

// Funko head is flat and frontal: lift it so it reads the same tone as the rounded body
const baseColors = (zoneArr, headId) => {
  const c = new Float32Array(zoneArr.length * 3).fill(1);
  for (let i = 0; i < zoneArr.length; i++) if (zoneArr[i] === headId) c.fill(1.8, i * 3, i * 3 + 3);
  return c;
};

const muscleGeometry = (geo) => {
  const g = geo.clone();
  const p = g.attributes.position.array, n = g.attributes.normal.array;
  const col = new Float32Array(p.length);
  for (let i = 0; i < p.length; i += 3) {
    for (let k = 0; k < 3; k++) p[i + k] -= n[i + k] * 0.0035;
    const stripe = 0.5 + 0.5 * Math.sin((p[i + 1] * 0.8 + p[i] * 0.35 + p[i + 2] * 0.2) * 520);
    const f = 0.72 + 0.28 * stripe;
    col[i] = 0.66 * f; col[i + 1] = 0.16 * f; col[i + 2] = 0.17 * f;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
};
const HOVER = new THREE.Color("#5fc4b8");
const SEL = new THREE.Color("#f08a4b");

const organOf = (obj) => {
  for (let o = obj; o; o = o.parent) if (o.userData.organ) return o.userData.organ;
  return null;
};

const parseKey = (key) => {
  const [zone, side] = key.split("-");
  return { zone, side: side || null };
};

const useBodyGeometry = (model, age) => useMemo(() => {
  if (!model) return null;
  const pos = blend(model, age, "pos");
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setIndex(new THREE.BufferAttribute(model.arr.index, 1));
  g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(pos.length), 3));
  g.setAttribute("uv", new THREE.BufferAttribute(model.arr.uv, 2));
  g.computeVertexNormals();
  smoothSeams(g, model.arr.orig);
  const eyes = new THREE.BufferGeometry();
  const ep = blend(model, age, "eyes");
  eyes.setAttribute("position", new THREE.BufferAttribute(ep, 3));
  eyes.setIndex(new THREE.BufferAttribute(model.arr.eyes_index, 1));
  eyes.computeVertexNormals();
  eyes.setAttribute("color", new THREE.BufferAttribute(eyeColors(ep), 3));
  return { body: g, eyes };
}, [model, age]);

// Average normals of UV-split duplicates so seams stay invisible
function smoothSeams(g, orig) {
  const n = g.attributes.normal.array, acc = {};
  for (let i = 0; i < orig.length; i++) {
    const a = (acc[orig[i]] ||= [0, 0, 0]);
    a[0] += n[i * 3]; a[1] += n[i * 3 + 1]; a[2] += n[i * 3 + 2];
  }
  for (let i = 0; i < orig.length; i++) {
    const a = acc[orig[i]], l = Math.hypot(a[0], a[1], a[2]) || 1;
    n[i * 3] = a[0] / l; n[i * 3 + 1] = a[1] / l; n[i * 3 + 2] = a[2] / l;
  }
}

function eyeColors(p) {
  const n = p.length / 3, half = n / 2, col = new Float32Array(p.length);
  [0, half].forEach((start) => {
    let c = [0, 0, 0], maxZ = -Infinity, minZ = Infinity;
    for (let i = start; i < start + half; i++) { for (let k = 0; k < 3; k++) c[k] += p[i * 3 + k] / half; maxZ = Math.max(maxZ, p[i * 3 + 2]); minZ = Math.min(minZ, p[i * 3 + 2]); }
    const r = (maxZ - minZ) / 2;
    for (let i = start; i < start + half; i++) {
      const f = (p[i * 3 + 2] - c[2]) / r;
      const rgb = f > 0.93 ? [0.02, 0.015, 0.015] : f > 0.86 ? [0.24, 0.13, 0.06] : f > 0.78 ? [0.42, 0.27, 0.13] : [0.96, 0.94, 0.91];
      col.set(rgb, i * 3);
    }
  });
  return col;
}

// Zone centroid + surface hotspot per sub-zone
const eyeSpot = (eyes) => {
  const p = eyes.attributes.position.array, n = p.length / 6;
  const c = [0, 0, 0];
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) c[k] += p[i * 3 + k] / n;
  return [c[0], c[1] + 0.012, c[2] + 0.02];
};

const zoneInfo = (geo, zones, zoneArr, key, eyes, sex) => {
  const zi = zones.indexOf(key);
  const p = geo.attributes.position.array;
  const ids = [];
  for (let i = 0; i < zoneArr.length; i++) if (zoneArr[i] === zi) ids.push(i);
  const c = new THREE.Vector3();
  ids.forEach((i) => c.add(new THREE.Vector3(p[i * 3], p[i * 3 + 1], p[i * 3 + 2])));
  c.divideScalar(ids.length || 1);
  let size = 0;
  ids.forEach((i) => { size = Math.max(size, c.distanceTo(new THREE.Vector3(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]))); });
  const { zone, side } = parseKey(key);
  const sg = side === "right" ? -1 : 1;
  const subs = (SUBZONES[zone === "genitals" ? `genitals_${sex}` : zone] || []).filter((s) => !(sex === "male" && s === "breast"));
  const hotspots = subs.map((s) => {
    if (s === "eyes") return { id: s, pos: eyeSpot(eyes), normal: new THREE.Vector3(0, 0, 1) };
    const [o, u, f] = SUB_DIRS[s] || [0, 0, 1];
    const d = new THREE.Vector3(o * sg, u, f).normalize();
    let best = -Infinity, bi = ids[0];
    ids.forEach((i) => {
      const v = (p[i * 3] - c.x) * d.x + (p[i * 3 + 1] - c.y) * d.y + (p[i * 3 + 2] - c.z) * d.z;
      if (v > best) { best = v; bi = i; }
    });
    const nm = geo.attributes.normal.array;
    return { id: s, pos: [p[bi * 3], p[bi * 3 + 1], p[bi * 3 + 2]], normal: new THREE.Vector3(nm[bi * 3], nm[bi * 3 + 1], nm[bi * 3 + 2]) };
  });
  return { center: c, size, hotspots };
};

const MAX_D = 5.2, MIN_D = 0.5;
const CameraRig = ({ focus, height, view }) => {
  const { camera, controls, size } = useThree();
  const narrow = size.width < size.height ? 1.45 : 1;
  const goal = useRef(null);
  useEffect(() => {
    const back = view === "back";
    if (focus) {
      const dist = THREE.MathUtils.clamp(focus.size * 8.4, 0.75, 3) * narrow;
      const dir = new THREE.Vector3(0, 0.12, focus.back ? -1 : 1).normalize();
      goal.current = { target: focus.center.clone(), pos: focus.center.clone().add(dir.multiplyScalar(dist)) };
    } else {
      const t = new THREE.Vector3(0, height * 0.52, 0);
      goal.current = { target: t, pos: new THREE.Vector3(0, height * 0.55, back ? -4.4 : 4.4) };
    }
  }, [focus, height, view, narrow]);
  useFrame((_, dt) => {
    const g = goal.current;
    if (!g || !controls) return;
    const a = 1 - Math.exp(-dt * 5);
    camera.position.lerp(g.pos, a);
    controls.target.lerp(g.target, a);
    controls.update();
    if (camera.position.distanceTo(g.pos) < 0.005 && controls.target.distanceTo(g.target) < 0.005) goal.current = null;
  });
  return null;
};

// Fake subsurface scattering: warm rosy glow on thin edges (ears, nose, fingers)
const addSSS = (shader) => {
  shader.fragmentShader = shader.fragmentShader.replace("#include <emissivemap_fragment>",
    "#include <emissivemap_fragment>\nfloat sssF = pow(1.0 - clamp(dot(normalize(normal), normalize(vViewPosition)), 0.0, 1.0), 2.2);\ntotalEmissiveRadiance += vec3(0.75, 0.2, 0.14) * sssF * 0.14 * diffuseColor.rgb;");
};

const SkinMaterial = ({ sex, inner, faint }) => {
  const map = useLoader(THREE.TextureLoader, `/models/skin_${sex}.jpg?v=31`);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 16;
  return (
    <meshPhysicalMaterial key={inner ? "i" : "o"} map={map} vertexColors roughness={0.58} metalness={0}
      sheen={0.22} sheenRoughness={0.55} sheenColor="#ffb4a2" clearcoat={0.07} clearcoatRoughness={0.55} specularIntensity={0.55}
      onBeforeCompile={(sh) => { addSSS(sh); (inner ? clipInside : clipOutside)(sh); }} customProgramCacheKey={() => (inner ? "skin-in" : "skin-out")}
      transparent={inner} opacity={inner ? (faint ? 0.1 : 0.2) : 1} depthWrite={!inner} />
  );
};

// Pulsing pin on the exact skin point chosen in skin mode
const SkinMarker = ({ spot }) => {
  const ref = useRef();
  const q = useMemo(() => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(...spot.normal).normalize()), [spot]);
  useFrame(({ clock }) => { if (ref.current) ref.current.scale.setScalar(1 + 0.25 * Math.sin(clock.elapsedTime * 4)); });
  return (
    <group position={spot.point} quaternion={q} renderOrder={10}>
      <mesh ref={ref} position={[0, 0, 0.002]} raycast={() => null}>
        <ringGeometry args={[0.009, 0.014, 32]} />
        <meshBasicMaterial color="#f97316" transparent opacity={0.9} depthTest={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 0, 0.003]} raycast={() => null}>
        <circleGeometry args={[0.005, 24]} />
        <meshBasicMaterial color="#c2410c" depthTest={false} />
      </mesh>
    </group>
  );
};

const Body = ({ model, age, sex, height, layers, selection, onPick, subzone, setSubzone, setFocus, setItems, marker }) => {
  const geo = useBodyGeometry(model, age);
  const [hover, setHover] = useState(null);
  const { zones } = model.meta;
  const zoneArr = model.arr.zone;
  const selKey = selection?.key;

  const xray = !!selection && ["head", "neck"].includes(selection.zone);
  const show = (k) => !!selection && layers[k];
  const J = useMemo(() => blendJoints(model, age), [model, age]);
  const base = useMemo(() => baseColors(zoneArr, zones.indexOf("head")), [zoneArr, zones]);
  const showMuscles = show("muscles");
  const muscles = useMemo(() => (showMuscles ? muscleGeometry(geo.body) : null), [geo, showMuscles]);
  useEffect(() => {
    const col = geo.body.attributes.color;
    const si = zones.indexOf(selKey), hi = zones.indexOf(hover);
    for (let i = 0; i < zoneArr.length; i++) {
      const c = zoneArr[i] === si ? SEL : HOVER;
      const mix = zoneArr[i] === si ? 0.55 : zoneArr[i] === hi ? 0.35 : 0;
      const r = base[i * 3], g = base[i * 3 + 1], b = base[i * 3 + 2];
      col.setXYZ(i, r + (c.r - r) * mix, g + (c.g - g) * mix, b + (c.b - b) * mix);
    }
    col.needsUpdate = true;
  }, [geo, base, selKey, hover, zones, zoneArr]);

  const info = useMemo(() => (geo && selKey ? zoneInfo(geo.body, zones, zoneArr, selKey, geo.eyes, sex) : null), [geo, selKey, zones, zoneArr, sex]);
  useEffect(() => {
    if (info) { CLIP.center.value.copy(info.center); CLIP.radius.value = Math.max(info.size * 1.1, 0.12); }
    else CLIP.radius.value = 0;
  }, [info]);
  useEffect(() => () => { CLIP.radius.value = 0; }, []);
  useEffect(() => {
    setFocus(info ? { center: info.center, size: info.size, back: BACK_ZONES.includes(parseKey(selKey).zone) } : null);
  }, [info, selKey, setFocus]);

  const organs = useMemo(() => (selection && layers.organs ? organsFor(selection.zone, sex) : []), [selection, sex, layers.organs]);
  useEffect(() => {
    const k = height / REF_H;
    setItems([...(info?.hotspots || []), ...organs.map((o) => ({ id: o.id, organ: true, pos: o.label.map((v) => v * k) }))]);
  }, [info, organs, height, setItems]);
  const keyOf = (e) => zones[zoneArr[e.face.a]];
  const hasOrgans = show("organs") && organs.length > 0;
  return (
    <FaintLayers.Provider value={hasOrgans}>
    <group>
      <mesh geometry={geo.body} castShadow receiveShadow
        onPointerMove={(e) => { e.stopPropagation(); const k = keyOf(e); if (k !== hover) setHover(k); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(null); document.body.style.cursor = "auto"; }}
        onClick={(e) => {
          e.stopPropagation(); if (e.delta > 6) return;
          const org = show("organs") && e.intersections.map((i) => organOf(i.object)).find(Boolean);
          if (org) { setSubzone(org); return; }
          const key = keyOf(e); onPick({ key, ...parseKey(key), point: e.point.toArray(), normal: e.face.normal.toArray() }); }}>
        <SkinMaterial sex={sex} />
      </mesh>
      {info && (
        <mesh geometry={geo.body} raycast={() => null} renderOrder={5}>
          <SkinMaterial sex={sex} inner faint={hasOrgans} />
        </mesh>
      )}
      {muscles && (
        <mesh geometry={muscles} raycast={() => null}>
          <meshStandardMaterial vertexColors roughness={0.5} transparent opacity={hasOrgans ? 0.15 : layers.bones || layers.vessels ? 0.42 : 0.85} depthWrite={false} onBeforeCompile={clipInside} />
        </mesh>
      )}
      {show("bones") && <Skeleton J={J} />}
      {show("vessels") && <Arteries J={J} />}
      {show("organs") && <Organs organs={organs} height={height} sex={sex} subzone={subzone} />}
      {hasOrgans && info && <pointLight position={info.center.clone().add(new THREE.Vector3(0, 0.1, BACK_ZONES.includes(selection.zone) ? -0.6 : 0.6)).toArray()} intensity={1.6} distance={1.6} decay={1.5} color="#fff3e0" />}
      <Suspense fallback={null}><Hair sex={sex} age={age} xray={xray} /></Suspense>
      {marker && <SkinMarker spot={marker} />}
    </group>
    </FaintLayers.Provider>
  );
};

const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 640px), (pointer: coarse)").matches;
const keepContext = ({ gl }) => gl.domElement.addEventListener("webglcontextlost", (e) => e.preventDefault());

export const Body3D = memo(({ sex, age, view, layers, selection, onPick, lang, subzone, setSubzone, hideCallouts, paused, marker }) => {
  const [model, setModel] = useState(null);
  const [focus, setFocus] = useState(null);
  const [items, setItems] = useState([]);
  const refs = useCalloutRefs();
  useEffect(() => { let on = true; loadModel(sex).then((m) => on && setModel(m)); return () => { on = false; }; }, [sex]);
  const height = model ? bodyHeight(model, age) : 1.75;
  return (
    <div className="absolute inset-0" style={{ filter: "contrast(1.15) saturate(1.15)" }}>
    <Canvas data-testid="body-3d-canvas" camera={{ position: [0, 0.95, 4.4], fov: 38 }} shadows dpr={[1, 2]} gl={{ alpha: true, antialias: true }} frameloop={paused && IS_MOBILE ? "never" : "always"} onCreated={keepContext}>
      <hemisphereLight args={["#fffaf5", "#a89a90", 0.4]} />
      <directionalLight position={[1.8, 3.2, 3]} intensity={1.6} color="#fff1e4" castShadow
        shadow-mapSize={[2048, 2048]} shadow-bias={-0.0004} shadow-normalBias={0.025}
        shadow-camera-left={-1.2} shadow-camera-right={1.2} shadow-camera-top={2.2} shadow-camera-bottom={-0.2} shadow-camera-near={0.5} shadow-camera-far={9} />
      <directionalLight position={[-2.6, 1.6, 2]} intensity={0.55} color="#e8f0ff" />
      <directionalLight position={[-2.2, 2.4, -2.6]} intensity={1.8} color="#f4f8ff" />
      <directionalLight position={[2.4, 1.4, -2.6]} intensity={1.3} color="#fff4ea" />
      <Suspense fallback={null}><Environment preset="studio" environmentIntensity={0.22} /></Suspense>
      <Suspense fallback={null}>
        {model && <Body model={model} age={age} sex={sex} height={height} layers={layers} selection={selection} onPick={onPick} subzone={subzone} setSubzone={setSubzone} setFocus={setFocus} setItems={setItems} marker={marker} />}
        <ContactShadows position={[0, 0, 0]} opacity={0.55} scale={3} blur={2.6} far={1.2} color="#3b4656" resolution={1024} />
      </Suspense>
      <OrbitControls makeDefault enableDamping zoomToCursor minDistance={MIN_D} maxDistance={MAX_D} screenSpacePanning />
      <CameraRig focus={focus} height={height} view={view} />
      <CalloutProjector items={hideCallouts ? [] : items} refs={refs} />
    </Canvas>
    <CalloutOverlay items={hideCallouts ? [] : items} refs={refs} lang={lang} subzone={subzone} setSubzone={setSubzone} />
    </div>
  );
});
